import { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ChatResponseToolbar from './ChatResponseToolbar';
import CopyIcon from '@mui/icons-material/ContentCopy';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SourceDocument from '../../Voyager/Chat/SourceDocument';
import type { ChatItem, ChatItemUpdateRequest } from '@/types/apiTypes';
import type { EntityId } from '@/types';

interface ChatLineProps {
  line: ChatItem | { role: 'assistant'; content: string };
  updateChatLine: (id: EntityId, data: ChatItemUpdateRequest) => Promise<void>;
}

function ChatLine({ line, updateChatLine }: ChatLineProps) {
  const isAssistant = line.role === 'assistant';
  const [tooltip, setTooltip] = useState('Copy');
  const [showSources, setShowSources] = useState(false);
  const setCopied = () => setTooltip('Copied!');
  const setCopyError = () => setTooltip('Copy Error!');

  const copyText = () => {
    if ('clipboard' in navigator) {
      navigator.clipboard
        .writeText(line.content.trim())
        .then(setCopied, setCopyError);
    } else {
      setCopyError();
    }
  };

  const handleToggleSources = () => {
    setShowSources(!showSources);
  };

  const getSources = (): Array<{
    content: string;
    title: string;
    certainty: number;
    start_line?: number;
    end_line?: number;
  }> => {
    if (!('sources' in line) || !line.sources) return [];
    try {
      return JSON.parse(line.sources) as Array<{
        content: string;
        title: string;
        certainty: number;
        start_line?: number;
        end_line?: number;
      }>;
    } catch (error) {
      console.log('Error parsing sources:', error);
      return [];
    }
  };

  return (
    <Box
      sx={{
        // display: 'flex',
        justifyContent: isAssistant ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        variant='outlined'
        sx={{
          p: 2,
          // backgroundColor: isBot ? "primary.light" : "secondary.light",
          borderRadius: isAssistant
            ? '20px 20px 5px 20px'
            : '20px 20px 20px 5px',
          justifyContent: isAssistant ? 'flex-end' : 'flex-start',
        }}
      >
        <Grid container>
          <Grid size={12}>
            <Box sx={{ display: 'flex', justifyContent: isAssistant ? 'flex-end' : 'flex-start', alignItems: 'center', gap: 1 }}>
              <Typography variant='overline'>{line.role}</Typography>
              {isAssistant ? (
                <Tooltip title={tooltip}>
                  <IconButton aria-label='close' onClick={copyText}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              ) : undefined}
            </Box>
          </Grid>
          <Grid size={12}>
            <Markdown remarkPlugins={[remarkGfm]}>
              {line.content.trim()}
            </Markdown>
          </Grid>
          {isAssistant && 'entity_id' in line ? (
            <Grid size={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ChatResponseToolbar
                  line={line}
                  updateChatLine={updateChatLine}
                  showSources={showSources}
                  onToggleSources={handleToggleSources}
                />
              </Box>
            </Grid>
          ) : undefined}
        </Grid>
        {isAssistant && showSources && getSources().length > 0 && 'entity_id' in line && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mt: 1 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Sources:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {getSources().map((doc, index) => (
                  <SourceDocument key={`source-${index}`} doc={doc} />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default ChatLine;

